// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// FRONT-END-ONLY SEED DATA (Floor plan · per-rack inlet temps °F). Shaped
// after the mockup's floor view. In future this should be derived from the
// digital-twin telemetry rather than hardcoded — see
// docs/design/hardcoded-frontend-data.md.

import type { FloorPlan } from '../types/alarm';

/**
 * Temperature → color band thresholds (°F), matching the mockup's tempColor():
 *   <68 tcool · 68–75 tok · 76–83 twarn · 84–89 thot · ≥90 tcrit
 */
export function tempBand(t: number): 'tcool' | 'tok' | 'twarn' | 'thot' | 'tcrit' {
  if (t >= 90) return 'tcrit';
  if (t >= 84) return 'thot';
  if (t >= 76) return 'twarn';
  if (t >= 68) return 'tok';
  return 'tcool';
}

const ATL_FLOOR: FloorPlan = {
  siteId: 'atl',
  note: 'Physical layout · per-rack inlet temps °F. CRAH-1 supply drift is warming Row B in Data Hall A.',
  footer: 'PF 0.90 · THD 2.1%',
  rooms: [
    { name: 'Chiller Plant', temp: 78 },
    { name: 'UPS Room A', temp: 72 },
    { name: 'UPS Room B', temp: 79 },
    { name: 'Electrical Room A', temp: 74 },
    { name: 'Gen Yard', temp: 71 },
    { name: 'NOC', temp: 70 },
  ],
  halls: [
    {
      id: 'hall-a',
      name: 'Data Hall A',
      crah: [
        { label: 'CRAH-1', status: 'warn' },
        { label: 'CRAH-2', status: 'ok' },
      ],
      rows: [
        {
          label: 'Row A',
          cells: [
            { id: 'A-1', temp: 72 },
            { id: 'A-2', temp: 73 },
            { id: 'A-3', temp: 73 },
            { id: 'A-4', temp: 74 },
            { id: 'A-5', temp: 72 },
            { id: 'A-6', temp: 73 },
          ],
        },
        {
          label: 'Row B',
          cells: [
            { id: 'A-11', temp: 74 },
            { id: 'A-12', temp: 73 },
            { id: 'A-13', temp: 74 },
            { id: 'A-14', temp: 78 },
            { id: 'A-15', temp: 74 },
            { id: 'A-16', temp: 72 },
          ],
        },
      ],
    },
    {
      id: 'hall-b',
      name: 'Data Hall B',
      crah: [
        { label: 'CRAH-3', status: 'ok' },
        { label: 'CRAH-4', status: 'ok' },
      ],
      rows: [
        {
          label: 'Row C',
          cells: [
            { id: 'B-1', temp: 71 },
            { id: 'B-2', temp: 72 },
            { id: 'B-3', temp: 72 },
            { id: 'B-4', temp: 73 },
            { id: 'B-5', temp: 71 },
            { id: 'B-6', temp: 72 },
          ],
        },
        {
          label: 'Row D',
          cells: [
            { id: 'B-11', temp: 72 },
            { id: 'B-12', temp: 73 },
            { id: 'B-13', temp: 74 },
            { id: 'B-14', temp: 73 },
            { id: 'B-15', temp: 72 },
            { id: 'B-16', temp: 71 },
          ],
        },
      ],
    },
  ],
};

export const FLOOR_PLANS: Record<string, FloorPlan> = {
  atl: ATL_FLOOR,
};
